import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Check, Save, LogOut, Building2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { useApp } from "../context/AppContext";
import { useLang } from "../lib/i18n";
import { api } from "../lib/api";

// Assistant progressif — schéma piloté par données, libellés via i18n (FR/EN/ES)
const OUI_NON = [{ v: "oui", k: "yes" }, { v: "non", k: "no" }];
const STEPS = [
  { id: "entreprise", title: "Votre entreprise", fields: [
    { k: "nom", label: "Nom de l'entreprise", type: "text" },
    { k: "secteur", label: "Secteur d'activité", type: "text" },
    { k: "sous_secteur", label: "Sous-secteur", type: "text" },
    { k: "annee_creation", label: "Année de création", type: "number" },
    { k: "localisation", label: "Localisation", type: "text" },
    { k: "nb_salaries", label: "Nombre de salariés", type: "number" },
    { k: "nb_points_vente", label: "Nombre de points de vente", type: "number" },
    { k: "vente_physique", label: "Vente physique ?", type: "toggle" },
    { k: "vente_ligne", label: "Vente en ligne ?", type: "toggle" },
    { k: "saisonnier", label: "Activité saisonnière ?", type: "toggle" },
    { k: "a_du_stock", label: "Gérez-vous du stock ?", type: "toggle" },
    { k: "concurrents", label: "Principaux concurrents", type: "text" },
  ]},
  { id: "ventes", title: "Vos ventes", fields: [
    { k: "ca_annuel", label: "Chiffre d'affaires annuel (€)", type: "number" },
    { k: "ca_n1", label: "CA N-1 (€)", type: "number" },
    { k: "objectif_ca", label: "Objectif de CA annuel (€)", type: "number" },
    { k: "tickets", label: "Nombre de tickets / transactions par mois", type: "number" },
    { k: "panier_moyen", label: "Panier moyen (€)", type: "number" },
    { k: "trafic", label: "Trafic / fréquentation par mois", type: "number" },
    { k: "evolution_ca", label: "Évolution du CA (%)", type: "number" },
  ]},
  { id: "rentabilite", title: "Votre rentabilité", fields: [
    { k: "marge_taux", label: "Taux de marge (%)", type: "number" },
    { k: "marge_euros", label: "Marge en euros (€)", type: "number" },
    { k: "marge_n1", label: "Marge N-1 (%)", type: "number" },
    { k: "objectif_marge", label: "Objectif de marge (%)", type: "number" },
    { k: "remise_moyenne", label: "Niveau moyen de remise (%)", type: "number" },
    { k: "produits_rentables", label: "Produits/services les plus rentables", type: "text" },
  ]},
  { id: "clients", title: "Vos clients", fields: [
    { k: "clients_actifs", label: "Nombre de clients actifs", type: "number" },
    { k: "nouveaux_clients", label: "Nouveaux clients / mois", type: "number" },
    { k: "frequence_achat", label: "Fréquence d'achat (par an)", type: "number" },
    { k: "taux_fidelisation", label: "Taux de fidélisation (%)", type: "number" },
    { k: "taille_fichier", label: "Taille du fichier clients", type: "number" },
    { k: "repartition", label: "Répartition particuliers / professionnels", type: "text" },
  ]},
  { id: "stock", title: "Votre stock", pillar: "stock", fields: [
    { k: "stock_valeur", label: "Valeur du stock (€)", type: "number" },
    { k: "stock_pct_ca", label: "Stock en % du CA", type: "number" },
    { k: "rotation", label: "Rotation du stock (par an)", type: "number" },
    { k: "ruptures", label: "Ruptures fréquentes ?", type: "toggle" },
    { k: "surstock", label: "Surstock ?", type: "toggle" },
    { k: "nb_references", label: "Nombre de références", type: "number" },
  ]},
  { id: "finances", title: "Vos finances", fields: [
    { k: "loyer", label: "Loyer mensuel (€)", type: "number" },
    { k: "masse_salariale", label: "Masse salariale mensuelle (€)", type: "number" },
    { k: "cout_achats", label: "Coût des achats (€)", type: "number" },
    { k: "budget_marketing", label: "Budget marketing mensuel (€)", type: "number" },
    { k: "resultat_exploitation", label: "Résultat d'exploitation (€)", type: "number" },
    { k: "tresorerie", label: "Situation de trésorerie", type: "select",
      options: [{ v: "confortable", l: "Confortable" }, { v: "correcte", l: "Correcte" }, { v: "tendue", l: "Tendue" }, { v: "critique", l: "Critique" }] },
  ]},
  { id: "equipe", title: "Votre équipe", fields: [
    { k: "nb_salaries_eq", label: "Nombre de salariés", type: "number" },
    { k: "nb_managers", label: "Nombre de managers", type: "number" },
    { k: "turnover", label: "Turnover (%)", type: "number" },
    { k: "objectifs_definis", label: "Objectifs commerciaux définis ?", type: "toggle" },
    { k: "entretiens_reguliers", label: "Entretiens réguliers ?", type: "toggle" },
    { k: "difficulte_rh", label: "Principale difficulté RH", type: "text" },
  ]},
  { id: "visibilite", title: "Votre visibilité", fields: [
    { k: "site_web", label: "Site Internet (URL)", type: "text" },
    { k: "google_business", label: "Fiche Google Business ?", type: "toggle" },
    { k: "instagram", label: "Instagram (@compte)", type: "text" },
    { k: "abonnes", label: "Nombre d'abonnés (total)", type: "number" },
    { k: "frequence_publication", label: "Fréquence de publication / semaine", type: "number" },
    { k: "avis_nombre", label: "Nombre d'avis Google", type: "number" },
    { k: "note_google", label: "Note Google (/5)", type: "number" },
    { k: "programme_fidelite", label: "Programme de fidélité ?", type: "toggle" },
  ]},
  { id: "objectifs", title: "Vos objectifs à 90 jours", isObjectives: true, fields: [] },
];

const OBJECTIFS = [
  { id: "ca", canon: "Augmenter le chiffre d'affaires" }, { id: "marge", canon: "Augmenter la marge" },
  { id: "trafic", canon: "Augmenter le trafic" }, { id: "panier", canon: "Augmenter le panier moyen" },
  { id: "fideliser", canon: "Fidéliser davantage" }, { id: "acquerir", canon: "Acquérir de nouveaux clients" },
  { id: "treso", canon: "Améliorer la trésorerie" }, { id: "stock", canon: "Réduire le stock" },
  { id: "ruptures", canon: "Réduire les ruptures" }, { id: "visibilite", canon: "Améliorer la visibilité" },
  { id: "reputation", canon: "Améliorer la réputation" }, { id: "orga", canon: "Améliorer l'organisation" },
  { id: "equipe", canon: "Améliorer les performances de l'équipe" }, { id: "temps", canon: "Gagner du temps" },
];

export default function CompanyProfile360() {
  const nav = useNavigate();
  const { data } = useApp();
  const { t } = useLang();
  const [values, setValues] = useState({});
  const [objectives, setObjectives] = useState([]);
  const [step, setStep] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [finished, setFinished] = useState(false);

  const hasStock = values.a_du_stock !== "non";
  const steps = useMemo(() => STEPS.filter(s => s.pillar !== "stock" || hasStock), [hasStock]);

  useEffect(() => {
    (async () => {
      try {
        const { data: doc } = await api.get("/company-profile");
        const base = doc?.data || {};
        // Prefill depuis les données déjà connues (onboarding / réputation / fidélité)
        const pre = {
          nom: base.nom ?? data?.onboarding?.business_name ?? "",
          secteur: base.secteur ?? data?.onboarding?.activity_type ?? "",
          panier_moyen: base.panier_moyen ?? data?.onboarding?.avg_basket ?? "",
          marge_taux: base.marge_taux ?? data?.onboarding?.gross_margin ?? "",
          avis_nombre: base.avis_nombre ?? data?.reputation?.reviews ?? "",
          note_google: base.note_google ?? data?.reputation?.rating ?? "",
          clients_actifs: base.clients_actifs ?? data?.loyalty?.clients ?? "",
        };
        const merged = { ...pre, ...base };
        setValues(merged);
        setObjectives(doc?.objectives || []);
        // Borne l'étape reprise sur les étapes réellement affichées après filtrage
        // (une entreprise sans stock n'a que 8 étapes, pas 9).
        const filteredLen = STEPS.filter(s => s.pillar !== "stock" || merged.a_du_stock !== "non").length;
        setStep(Math.min(doc?.step || 0, filteredLen - 1));
      } catch { /* profil vide */ }
      setLoaded(true);
    })();
    // eslint-disable-next-line
  }, []);

  const relevantFields = useMemo(() => steps.filter(s => !s.isObjectives).flatMap(s => s.fields.map(f => f.k)), [steps]);
  const completion = useMemo(() => {
    const total = relevantFields.length + 1; // +1 pour les objectifs
    let filled = relevantFields.filter(k => String(values[k] ?? "").trim() !== "").length;
    if (objectives.length) filled += 1;
    return Math.round((filled / total) * 100);
  }, [values, objectives, relevantFields]);

  const persist = async (opts = {}) => {
    try {
      await api.put("/company-profile", { data: values, step, objectives, completion, finalize: !!opts.finalize });
    } catch { toast.error(t("p360_toast_save_fail")); return false; }
    return true;
  };

  const setV = (k, v) => setValues(p => ({ ...p, [k]: v }));
  const toggleObjective = (o) => setObjectives(p => p.includes(o) ? p.filter(x => x !== o) : (p.length >= 3 ? p : [...p, o]));

  const current = steps[step];

  const next = async () => {
    const ok = await persist();
    if (!ok) return;
    if (step < steps.length - 1) { setStep(step + 1); window.scrollTo(0, 0); }
    else { await persist({ finalize: true }); setFinished(true); toast.success(t("p360_toast_saved")); }
  };
  const back = () => { if (step > 0) { setStep(step - 1); window.scrollTo(0, 0); } };
  const quit = async () => { await persist(); nav("/app/dashboard"); };

  if (!loaded) return <Layout><div className="max-w-xl mx-auto py-20 text-center text-[hsl(var(--muted-foreground))]">{t("p360_loading")}</div></Layout>;

  if (finished) {
    return (
      <Layout>
        <div className="max-w-xl mx-auto text-center py-10" data-testid="profile360-done">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-primary to-neon flex items-center justify-center mb-5"><Check size={32} className="text-white" /></div>
          <h1 className="font-display font-black text-2xl mb-2">{t("p360_done_title")}</h1>
          <p className="text-neon font-display font-black text-lg mb-4">{t("p360_done_completed", { c: completion })}</p>
          <div className="lb-card p-4 text-left mb-4">
            <p className="font-semibold mb-1">{values.nom || t("p360_done_your_co")}</p>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">{[values.secteur, values.localisation].filter(Boolean).join(" · ")}</p>
            <div className="grid grid-cols-3 gap-2 mt-3 text-center">
              {[[t("p360_ca"), values.ca_annuel && `${values.ca_annuel} €`], [t("p360_marge"), values.marge_taux && `${values.marge_taux} %`], [t("p360_panier"), values.panier_moyen && `${values.panier_moyen} €`]].map(([l, v], i) => (
                <div key={i} className="rounded-xl bg-white/[0.03] border border-white/5 p-2"><p className="text-[10px] uppercase text-[hsl(var(--muted-foreground))]">{l}</p><p className="text-sm font-semibold">{v || "—"}</p></div>
              ))}
            </div>
          </div>
          {objectives.length > 0 && (
            <div className="lb-card p-4 text-left mb-4">
              <p className="text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2">{t("p360_done_priorities")}</p>
              {objectives.map((o, i) => { const it = OBJECTIFS.find((x) => x.canon === o); return <p key={i} className="text-sm">• {it ? t(`p360o_${it.id}`) : o}</p>; })}
            </div>
          )}
          {completion < 100 && <p className="text-xs text-[hsl(var(--muted-foreground))] mb-4">{t("p360_done_incomplete")}</p>}
          <div className="lb-card p-4 text-left mb-4 border-neon/20" data-testid="profile360-enrich-docs">
            <p className="font-semibold text-sm mb-1">{t("diag_enrich_title")}</p>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">{t("diag_enrich_text")}</p>
            <Button onClick={() => nav("/app/documents")} variant="outline" className="w-full rounded-full h-10 border-neon/30 bg-neon/5 text-sm" data-testid="profile360-enrich-cta">{t("diag_enrich_btn")}</Button>
            <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-2 text-center">{t("diag_enrich_optional")}</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => nav("/app/diagnostic")} data-testid="profile360-see-diag" className="flex-1 rounded-full h-12 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn font-semibold">{t("p360_see_diag")} <ArrowRight size={16} className="ml-1.5" /></Button>
            <Button onClick={() => { setFinished(false); setStep(0); }} variant="outline" className="rounded-full h-12 px-4 border-white/10 bg-white/5" data-testid="profile360-edit">{t("p360_edit")}</Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-xl mx-auto" data-testid="profile360">
        <div className="flex items-center gap-2 mb-1"><Building2 size={18} className="text-neon" /><h1 className="font-display font-black text-xl">{t("p360_title")}</h1></div>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">{t("p360_step_of", { s: step + 1, n: steps.length, c: completion })}</p>
        <div className="h-2 rounded-full bg-white/10 overflow-hidden mb-5"><div className="h-full bg-gradient-to-r from-primary to-neon transition-all" style={{ width: `${completion}%` }} data-testid="profile360-progress" /></div>

        <motion.div key={current.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="lb-card p-5">
          <h2 className="font-display font-bold text-lg mb-4">{t(`p360s_${current.id}`)}</h2>

          {current.isObjectives ? (
            <div>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">{t("p360_obj_help")}</p>
              <div className="grid grid-cols-1 gap-2">
                {OBJECTIFS.map((o) => {
                  const on = objectives.includes(o.canon);
                  return (
                    <button key={o.id} onClick={() => toggleObjective(o.canon)} data-testid={`obj-${o.id}`}
                      className={`text-left text-sm rounded-xl border p-3 transition-colors ${on ? "border-neon/50 bg-neon/10 text-white" : "border-white/10 bg-white/[0.02] text-[hsl(var(--muted-foreground))]"}`}>
                      <span className="inline-flex items-center gap-2">{on && <Check size={14} className="text-neon" />}{t(`p360o_${o.id}`)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {current.fields.map((f) => (
                <div key={f.k}>
                  <label className="block text-sm font-medium mb-1.5">{t(`p360f_${f.k}`)}</label>
                  {f.type === "toggle" ? (
                    <div className="flex gap-2">
                      {OUI_NON.map(o => (
                        <button key={o.v} onClick={() => setV(f.k, o.v)} data-testid={`fld-${f.k}-${o.v}`}
                          className={`flex-1 rounded-xl border py-2.5 text-sm transition-colors ${values[f.k] === o.v ? "border-neon/50 bg-neon/10 text-white" : "border-white/10 bg-white/[0.02] text-[hsl(var(--muted-foreground))]"}`}>{t(o.k)}</button>
                      ))}
                    </div>
                  ) : f.type === "select" ? (
                    <select value={values[f.k] ?? ""} onChange={(e) => setV(f.k, e.target.value)} data-testid={`fld-${f.k}`}
                      className="w-full rounded-xl bg-white/[0.03] border border-white/10 px-3 py-2.5 text-sm text-white focus:border-neon/50 outline-none">
                      <option value="">{t("p360_select")}</option>
                      {f.options.map(o => <option key={o.v} value={o.v} className="bg-[#121218]">{t(`p360_treso_${o.v}`)}</option>)}
                    </select>
                  ) : (
                    <input type={f.type === "number" ? "number" : "text"} value={values[f.k] ?? ""} onChange={(e) => setV(f.k, e.target.value)} data-testid={`fld-${f.k}`}
                      className="w-full rounded-xl bg-white/[0.03] border border-white/10 px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-neon/50 outline-none" placeholder={t("p360_optional")} />
                  )}
                </div>
              ))}
            </div>
          )}
        </motion.div>

        <div className="flex items-center gap-2 mt-4">
          <Button onClick={back} disabled={step === 0} variant="outline" className="rounded-full h-11 px-4 border-white/10 bg-white/5 disabled:opacity-40" data-testid="profile360-back"><ChevronLeft size={16} /></Button>
          <Button onClick={next} className="flex-1 rounded-full h-11 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn font-semibold" data-testid="profile360-next">
            {step < steps.length - 1 ? <>{t("p360_save_continue")} <ChevronRight size={16} className="ml-1" /></> : <>{t("p360_finish")} <Check size={16} className="ml-1" /></>}
          </Button>
        </div>
        <button onClick={quit} className="w-full mt-3 min-h-[44px] sm:min-h-0 text-xs text-[hsl(var(--muted-foreground))] flex items-center justify-center gap-1 hover:text-white" data-testid="profile360-quit"><LogOut size={13} /> {t("p360_save_quit")}</button>
      </div>
    </Layout>
  );
}
